import React, { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { registerUserWithEmails } from '../../graphqlService';
import AuthLayout from './AuthLayout';
import PasswordField from './PasswordField';
import TermsAndConditions from '../Legal/TermsAndConditions';
import { isPasswordValid } from '../../utils/passwordPolicy';
import { debugLog } from '../../utils/debugLog';
import { AuthErrorCopy, describeAuthError } from './authErrors';
import { FIELD_ERRORS, FieldErrors, focusFirstInvalid, normalizeEmail, SUPPORT_HREF, validateEmail } from './authForm';

/**
 * How long the sign-up page waits for the registration call (which sends the
 * verification email before it answers) before moving on to the
 * verification page anyway. The call keeps running in the background.
 */
export const REGISTRATION_WAIT_MS = 8000;

type RegistrationOutcome =
  | { kind: 'done'; result: { success?: boolean; emailSent?: boolean | null } }
  | { kind: 'failed'; error: unknown }
  | { kind: 'slow' };

type SignupField = 'name' | 'email' | 'password' | 'confirmPassword' | 'terms';
const FIELD_ORDER: readonly SignupField[] = ['name', 'email', 'password', 'confirmPassword', 'terms'];

const TERMS_REQUIRED = 'Agree to the Terms and Conditions to create your account.';
const EMAIL_NOT_SENT = "Your account is ready, but we couldn't send the verification email. Resend it below.";

const Signup: React.FC = () => {
  // Signup never asked for a name, so every creator was greeted "Creator",
  // their profile read "Unknown User", and editor invitations arrived "from"
  // a bare email address.
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors<SignupField>>({});
  const [failure, setFailure] = useState<AuthErrorCopy | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const refs = {
    name: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    confirmPassword: useRef<HTMLInputElement>(null),
    terms: useRef<HTMLInputElement>(null),
  };

  const clearFieldError = (...fields: SignupField[]) => {
    if (fields.some((field) => fieldErrors[field])) {
      setFieldErrors((current) => {
        const next = { ...current };
        fields.forEach((field) => delete next[field]);
        return next;
      });
    }
  };

  const validate = (): FieldErrors<SignupField> => {
    const errors: FieldErrors<SignupField> = {};
    if (!fullName.trim()) errors.name = FIELD_ERRORS.nameRequired;
    const emailProblem = validateEmail(email);
    if (emailProblem) errors.email = emailProblem;
    if (!password) errors.password = FIELD_ERRORS.newPasswordRequired;
    else if (!isPasswordValid(password)) errors.password = FIELD_ERRORS.newPasswordWeak;
    if (!confirmPassword) errors.confirmPassword = FIELD_ERRORS.confirmRequired;
    else if (password !== confirmPassword) errors.confirmPassword = FIELD_ERRORS.confirmMismatch;
    if (!agreeToTerms) errors.terms = TERMS_REQUIRED;
    return errors;
  };

  const handleSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setFailure(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      focusFirstInvalid(FIELD_ORDER, errors, refs);
      return;
    }

    const normalizedEmail = normalizeEmail(email);
    const displayName = fullName.trim();
    setLoading(true);

    try {
      // Create new user
      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;

      // Store the name on the Firebase profile too: displayName is what the
      // header, dashboard greeting and outgoing invitations read.
      try {
        await updateProfile(user, { displayName });
      } catch (profileError) {
        console.warn('Could not set display name on the new account:', profileError);
      }

      debugLog('User created successfully. User ID:', user.uid);

      // The backend mints the HMAC-signed verification token server-side;
      // the client-supplied token is unused.
      const verificationToken = '';

      // Use our professional email service for registration.
      //
      // The backend only answers once the email provider has accepted the
      // verification email, which can take a long time; the page used to sit
      // on "Creating..." for all of it. Wait a bounded time, then move on.
      debugLog('Starting professional registration flow...');
      let slowTimer: ReturnType<typeof setTimeout> | undefined;
      const outcome = await Promise.race<RegistrationOutcome>([
        registerUserWithEmails(
          user.uid,
          normalizedEmail,
          displayName,
          'creator',
          true, // Send welcome email
          verificationToken
        ).then(
          (result): RegistrationOutcome => ({ kind: 'done', result }),
          (error): RegistrationOutcome => ({ kind: 'failed', error })
        ),
        new Promise<RegistrationOutcome>((resolve) => {
          slowTimer = setTimeout(() => resolve({ kind: 'slow' }), REGISTRATION_WAIT_MS);
        }),
      ]);
      if (slowTimer) clearTimeout(slowTimer);

      // The account exists in every case below, and the verification page
      // has the resend action. Its one message says honestly whether the
      // verification email went out: it can fail (rate limit, provider
      // outage) while the account itself is created fine.
      if (outcome.kind === 'slow') {
        navigate('/verify-email', {
          state: {
            email: normalizedEmail,
            message: "Your account is ready. If the verification email doesn't arrive within a minute, resend it below.",
          },
        });
      } else if (outcome.kind === 'done') {
        const registrationResult = outcome.result;
        debugLog('Professional registration result:', registrationResult);

        if (registrationResult.success) {
          const emailSent = registrationResult.emailSent !== false;
          navigate('/verify-email', {
            state: {
              email: normalizedEmail,
              emailSent,
              message: emailSent
                ? `Account created. We sent a verification link to ${normalizedEmail}.`
                : `Your account is ready, but we couldn't send the verification email to ${normalizedEmail}. Resend it below.`,
            },
          });
        } else {
          navigate('/verify-email', {
            state: { email: normalizedEmail, emailSent: false, message: EMAIL_NOT_SENT },
          });
        }
      } else {
        console.error('Professional email service error:', outcome.error);
        navigate('/verify-email', {
          state: { email: normalizedEmail, emailSent: false, message: EMAIL_NOT_SENT },
        });
      }
    } catch (error: unknown) {
      console.error('Signup error:', error);
      const copy = describeAuthError(error, 'sign-up');
      if (copy.field) {
        const fieldError = { [copy.field]: copy.message } as FieldErrors<SignupField>;
        setFieldErrors(fieldError);
        focusFirstInvalid(FIELD_ORDER, fieldError, refs);
      } else {
        setFailure(copy);
      }
    } finally {
      setLoading(false);
    }
  };

  const failureAction = (copy: AuthErrorCopy) => {
    if (copy.action === 'sign-in') {
      return (
        <Button component={RouterLink} to="/login" color="inherit" size="small">
          Sign in
        </Button>
      );
    }
    if (copy.action === 'contact-support') {
      return (
        <Button href={SUPPORT_HREF} color="inherit" size="small">
          Contact support
        </Button>
      );
    }
    return undefined;
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Organize footage, feedback, editors and revisions in one workspace."
    >
      {failure && (
        <Alert severity={failure.severity} sx={{ mb: 3 }} action={failureAction(failure)}>
          {failure.message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSignup} noValidate sx={{ display: 'grid', gap: 2.5 }}>
        <TextField
          inputRef={refs.name}
          id="fullName"
          name="fullName"
          label="Your name"
          autoComplete="name"
          autoFocus
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={fullName}
          onChange={(event) => {
            setFullName(event.target.value);
            clearFieldError('name');
          }}
          error={Boolean(fieldErrors.name)}
          helperText={fieldErrors.name || 'Shown to editors you work with'}
        />

        <TextField
          inputRef={refs.email}
          id="email"
          name="email"
          type="email"
          label="Email address"
          autoComplete="email"
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            clearFieldError('email');
          }}
          error={Boolean(fieldErrors.email)}
          helperText={fieldErrors.email}
        />

        <PasswordField
          ref={refs.password}
          id="password"
          name="password"
          label="Password"
          autoComplete="new-password"
          showRequirements
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            clearFieldError('password', 'confirmPassword');
          }}
          error={Boolean(fieldErrors.password)}
          helperText={fieldErrors.password}
        />

        <PasswordField
          ref={refs.confirmPassword}
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm password"
          autoComplete="new-password"
          required
          InputLabelProps={{ required: false }}
          fullWidth
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            clearFieldError('confirmPassword');
          }}
          error={Boolean(fieldErrors.confirmPassword)}
          helperText={fieldErrors.confirmPassword}
        />

        <FormControl error={Boolean(fieldErrors.terms)}>
          <FormControlLabel
            control={
              <Checkbox
                inputRef={refs.terms}
                checked={agreeToTerms}
                onChange={(event) => {
                  setAgreeToTerms(event.target.checked);
                  clearFieldError('terms');
                }}
                inputProps={{
                  'aria-describedby': fieldErrors.terms ? 'terms-error' : undefined,
                  'aria-invalid': fieldErrors.terms ? true : undefined,
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I agree to the{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={(event: React.MouseEvent) => {
                    // Inside the label: open the terms without ticking the box.
                    event.preventDefault();
                    setShowTermsDialog(true);
                  }}
                  sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                >
                  Terms and Conditions
                </Link>
              </Typography>
            }
          />
          {fieldErrors.terms && (
            <FormHelperText id="terms-error" sx={{ mt: 0 }}>
              {fieldErrors.terms}
            </FormHelperText>
          )}
        </FormControl>

        <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
          {loading ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
              Creating account…
            </>
          ) : (
            'Create account'
          )}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
          Already have an account?{' '}
          <Link component={RouterLink} to="/login" sx={{ fontWeight: 600 }}>
            Sign in
          </Link>
        </Typography>
      </Box>

      <TermsAndConditions
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        onAccept={() => {
          setAgreeToTerms(true);
          clearFieldError('terms');
        }}
        showAcceptButton={true}
      />
    </AuthLayout>
  );
};

export default Signup;
